/**
 * 「さっき通したところ（戻る）」（Issue #658）。入力画面はチェーン確認を基本的に自動で「チェーンしない」で通すので、
 * 今の区間（最後の人の答えより後ろ）で通したチェーン確認のうち発動できるカードがあったものを 1 行 1 枚（発動の候補ごと）で出す。
 * 行を押して選び、「戻って発動する」で確定する（戻ると、その確認から後ろの応答が消える＝1 回押しでは戻さない）。
 * 戻して発動するのはページ（onRewind(chance, option)。ocgCore の rewindTo → respond { chain: index }）。
 */
import { useState } from 'react';
import { playerColor, usePlayerColors } from './common';
import { CardRow } from './CardRows';
import { btn } from './RespondTab';

/**
 * 飛ばした機会（サーバーの input.skipped_chances。新しい順。label・rows（{ key, option, tile, text }）はサーバーが付ける。Issue #665）→ 表示の段
 */
export function skippedSections(chances = []) {
  return chances.map((chance) => ({ key: String(chance.seq), chance, player: chance.player === 1 ? 1 : 0, label: chance.label, rows: chance.rows ?? [] }));
}

export default function SkippedChances({ chances = [], names = {}, thumbs = {}, players = [], busy = false, onRewind }) {
  const [picked, setPicked] = useState(null);   // 選んだ行の key（確定は別のボタン）
  const colors = usePlayerColors();
  if (!chances.length) return null;
  const sections = skippedSections(chances);
  const pickedRow = sections.flatMap((s) => s.rows.map((r) => ({ ...r, section: s }))).find((r) => r.key === picked) ?? null;
  return (
    <div data-testid="skipped-chances" style={{ marginTop: 10, paddingTop: 6, borderTop: '1px dashed #2a3658', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontSize: 12, fontWeight: 800, opacity: 0.9 }}>さっき通したところ（戻る）</div>
      {sections.map((s) => (
        <div key={s.key} data-testid="skipped-section" data-seq={s.chance.seq} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <span style={{ width: 10, height: 10, borderRadius: 999, background: playerColor(s.player, colors) ?? colors[0], flexShrink: 0 }} />
            <span style={{ whiteSpace: 'nowrap' }}>{players[s.player]?.name ?? `P${s.player + 1}`}</span>
            <span style={{ opacity: 0.85, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</span>
          </div>
          {s.rows.map((r) => (
            <div key={r.key} data-testid="skipped-row" data-key={r.key}>
              <CardRow tile={r.tile} player={s.player} names={names} thumbs={thumbs} disabled={busy} selected={picked === r.key}
                badge={r.text} onTap={() => setPicked((k) => (k === r.key ? null : r.key))} />
            </div>
          ))}
        </div>
      ))}
      {pickedRow ? (
        <div data-testid="skipped-confirm" style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 8, borderRadius: 8, background: '#2a2340', scrollSnapAlign: 'start' }}>
          <div style={{ fontSize: 12 }}>
            {pickedRow.section.label}まで戻って <strong>{names[pickedRow.tile.code] ?? pickedRow.tile.code}</strong> を発動します。そこから後ろの応答は消えます。
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" disabled={busy} style={btn('plain')} onClick={() => setPicked(null)}>やめる</button>
            <button type="button" data-testid="skipped-rewind" disabled={busy} style={btn('danger', { grow: true })}
              onClick={() => { const { section, option } = pickedRow; setPicked(null); onRewind?.(section.chance, option); }}>戻って発動する</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
